import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchFacets } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import type { CreationModalStepProps } from '@app/src/components/CreationModal/CreationModalStep'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import useProviderType from '@app/src/hooks/useProviderType'
import { useFetchAssessmentSettings } from '@app/src/pages/Assessments/useFetchAssessmentSettings'
import HighlightImportantKpis, {
  useImportantKpisFooterContent,
} from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/HighlightImportantKpis/HighlightImportantKpis'
import { countSelectedKpis } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/HighlightImportantKpis/importantKpisLogic'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Provider } from '@app/src/types/organizations'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents, NotificationSeverity } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { Dialog, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import {
  buildDefaultFormValues,
  buildSubmissionPayload,
  canSubmit,
  getDefaultTargetLevel,
  getEligibleSelection,
  getExpectationAssessmentTemplates,
  isFutureDate,
  pickInitialAssessmentTemplateId,
} from './bulkCreateExpectationLogic'
import SelectAssessmentStep from './Steps/SelectAssessmentStep'
import SelectedCompaniesStep from './Steps/SelectedCompaniesStep'
import SetDeadlineStep from './Steps/SetDeadlineStep'
import SetTargetStep from './Steps/SetTargetStep'
import SummaryStep from './Steps/SummaryStep'
import type { BulkCreateExpectationFormData, BulkCreateExpectationSubmissionPayload } from './types'
import { BulkCreateExpectationFormField } from './types'

type BulkCreateExpectationModalProps = {
  open: boolean
  onClose: () => void
  initialProviders: Provider[]
  preselectedAssessmentTemplate?: AssessmentTemplate
}

type Question = Pick<
  CreationModalStepProps,
  | 'title'
  | 'description'
  | 'fieldnames'
  | 'canGoNext'
  | 'overrideButtonTextId'
  | 'isLoading'
  | 'hasSkipButton'
  | 'customOnClickSkip'
  | 'footerContent'
> & {
  children: React.ReactNode
}

const ImportantKpisFooterContent: React.FC = () => {
  const content = useImportantKpisFooterContent(BulkCreateExpectationFormField.ImportantKpis)
  return <>{content}</>
}

const BulkCreateExpectationModal: React.FC<BulkCreateExpectationModalProps> = ({
  open,
  onClose,
  initialProviders,
  preselectedAssessmentTemplate,
}) => {
  const { formatMessage } = useIntl()
  const { activeStep, setActiveStep, setTotalSteps } = useCreationModalProgress()
  const { showSnackbar } = useSnackbar()
  const { trackEvent } = useAmplitude()
  const { showErrorNotification } = useErrorNotification()
  const queryClient = useQueryClient()
  const history = useHistory()
  const providerType = useProviderType()
  const { multiAssessmentExpectation } = useFlags()
  const { assessmentSettings, isLoading: isLoadingAssessmentSettings } = useFetchAssessmentSettings()
  const { mutateAsync: createExpectations, isPending: isSubmitting } = useCreateResource<
    void,
    BulkCreateExpectationSubmissionPayload
  >()

  const connectedProviderIds = useMemo(
    () => initialProviders.filter(p => p.linkStatus === ProviderLinkStatus.Connected).map(p => p.id),
    [initialProviders],
  )

  const formMethods = useForm<BulkCreateExpectationFormData>({
    shouldUnregister: false,
    defaultValues: buildDefaultFormValues(connectedProviderIds),
  })
  const { handleSubmit, reset, watch, setValue, getValues } = formMethods

  // Subscribe to fields that gate navigation so canGoNext re-evaluates on changes.
  const selectedTemplateId = watch(BulkCreateExpectationFormField.AssessmentTemplateId)
  watch(BulkCreateExpectationFormField.TargetAssessmentLevel)
  watch(BulkCreateExpectationFormField.ProviderIds)
  watch(BulkCreateExpectationFormField.Deadline)
  watch(BulkCreateExpectationFormField.ImportantKpis)

  const { items, isLoading: isFetchingTemplate } = useFetchCollectionWithPost<AssessmentTemplate>({
    key: FetchKey.AssessmentTemplates,
    endpoint: endpoints.assessmentTemplateCollection,
    payload: {
      filter: [
        {
          name: 'assessmentType',
          filters: [{ value: AssessmentType.BaselineAssessment, operator: Operators.EqualTo }],
        },
      ],
      include: [],
      pagination: { itemsPerPage: 1, pageNumber: 1 },
    },
    options: { enabled: open },
  })

  const baselineTemplate = items?.[0]

  const assessmentTemplates = useMemo(
    () =>
      getExpectationAssessmentTemplates(
        baselineTemplate,
        assessmentSettings,
        preselectedAssessmentTemplate,
        Boolean(multiAssessmentExpectation),
      ),
    [baselineTemplate, assessmentSettings, preselectedAssessmentTemplate, multiAssessmentExpectation],
  )
  const selectedTemplate = assessmentTemplates.find(t => t.id === selectedTemplateId)

  const selectTemplate = (template: AssessmentTemplate) => {
    if (template.id === getValues(BulkCreateExpectationFormField.AssessmentTemplateId)) return
    setValue(BulkCreateExpectationFormField.AssessmentTemplateId, template.id)
    setValue(BulkCreateExpectationFormField.TargetAssessmentLevel, getDefaultTargetLevel(template.levels))
    // Highlighted KPIs are questions of the previous template
    setValue(BulkCreateExpectationFormField.ImportantKpis, [])
  }

  const providerIdFilters = useMemo(
    () => connectedProviderIds.map(id => ({ value: id, operator: Operators.EqualTo })),
    [connectedProviderIds],
  )

  const {
    facets: [expectationProviderFacets],
    isLoading: isLoadingCoveredProviders,
  } = useFetchFacets({
    key: [FetchKey.ExpectationCollection, 'coveredProviders', selectedTemplateId ?? undefined],
    endpoint: endpoints.expectationWithFacets,
    facetsParam: [{ name: 'providerId', isEnum: true }],
    filter: [
      {
        name: 'assessmentTemplateId',
        filters: [{ value: selectedTemplateId ?? undefined, operator: Operators.EqualTo }],
      },
      {
        name: 'providerId',
        filters: providerIdFilters,
      },
    ],
    options: { enabled: open && selectedTemplateId != null && connectedProviderIds.length > 0 },
  })

  // Providers that are already covered by an expectation based on the same assessment
  const coveredProviderIds = useMemo(
    () => new Set((expectationProviderFacets ?? []).map(f => Number(f.value))),
    [expectationProviderFacets],
  )

  // Reset form to current selection whenever the modal opens; reset everything on close.
  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({
        queryKey: [FetchKey.ExpectationCollection, 'coveredProviders'],
        refetchType: 'all',
      })
      reset(buildDefaultFormValues(connectedProviderIds))
    }
    return () => {
      setActiveStep(1)
      reset(buildDefaultFormValues([]))
    }
  }, [open])

  // Select the initial template once the templates resolve or when the modal re-opens.
  useEffect(() => {
    if (!open || selectedTemplateId != null) return
    // The offered templates depend on the settings, so choosing before they load could pick the wrong one
    if (multiAssessmentExpectation && isLoadingAssessmentSettings) return
    const initialTemplateId = pickInitialAssessmentTemplateId(assessmentTemplates, preselectedAssessmentTemplate?.id)
    const initialTemplate = assessmentTemplates.find(t => t.id === initialTemplateId)
    if (initialTemplate) selectTemplate(initialTemplate)
  }, [
    open,
    selectedTemplateId,
    assessmentTemplates,
    preselectedAssessmentTemplate,
    multiAssessmentExpectation,
    isLoadingAssessmentSettings,
  ])

  const onSubmit = async (values: BulkCreateExpectationFormData) => {
    if (isLoadingCoveredProviders) return
    const eligibleIds = getEligibleSelection(
      values[BulkCreateExpectationFormField.ProviderIds],
      initialProviders,
      values[BulkCreateExpectationFormField.AssessmentTemplateId],
      values[BulkCreateExpectationFormField.TargetAssessmentLevel],
      coveredProviderIds,
    )
    const payload = buildSubmissionPayload({ ...values, [BulkCreateExpectationFormField.ProviderIds]: eligibleIds })

    await createExpectations(
      { url: endpoints.bulkCreateExpectation, body: payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.ExpectationCollection] })
          trackEvent({
            name: AmplitudeTrackingEvents.Accessor.Expectation.BulkCreated,
            eventProps: {
              assessment_template_id: values[BulkCreateExpectationFormField.AssessmentTemplateId],
              provider_count: payload.providerIds.length,
              target_assessment_level: values[BulkCreateExpectationFormField.TargetAssessmentLevel],
              important_kpis_count: countSelectedKpis(values[BulkCreateExpectationFormField.ImportantKpis]),
            },
          })
          showSnackbar({
            message: formatMessage(
              { id: 'bulkCreateExpectation.notifications.success' },
              { count: payload.providerIds.length, providerType },
            ),
            severity: NotificationSeverity.success,
          })
          onClose()
          history.push(paths.sourcingExpectationsList) // Redirect user to expectations list after successful creation
        },
        onError: error => {
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  const QUESTIONS: Question[] = [
    {
      title: formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.title' }),
      description: formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.description' }, { providerType }),
      fieldnames: [],
      canGoNext: () => getValues(BulkCreateExpectationFormField.AssessmentTemplateId) != null,
      children: (
        <SelectAssessmentStep
          assessmentTemplates={assessmentTemplates}
          selectedTemplateId={selectedTemplateId}
          onSelect={selectTemplate}
          showComingSoon={!multiAssessmentExpectation}
        />
      ),
    },
    {
      title: formatMessage({ id: 'bulkCreateExpectation.steps.setTarget.title' }),
      description: formatMessage({ id: 'bulkCreateExpectation.steps.setTarget.description' }, { providerType }),
      fieldnames: [BulkCreateExpectationFormField.TargetAssessmentLevel],
      canGoNext: () => Number.isFinite(getValues(BulkCreateExpectationFormField.TargetAssessmentLevel)),
      children: <SetTargetStep assessmentTemplate={selectedTemplate} />,
    },
    {
      title: formatMessage({ id: 'bulkCreateExpectation.steps.selectedCompanies.title' }, { providerType }),
      fieldnames: [BulkCreateExpectationFormField.ProviderIds],
      canGoNext: () =>
        !isLoadingCoveredProviders &&
        getEligibleSelection(
          getValues(BulkCreateExpectationFormField.ProviderIds),
          initialProviders,
          getValues(BulkCreateExpectationFormField.AssessmentTemplateId),
          getValues(BulkCreateExpectationFormField.TargetAssessmentLevel),
          coveredProviderIds,
        ).length > 0,
      children: (
        <SelectedCompaniesStep
          providers={initialProviders}
          assessmentTemplate={selectedTemplate}
          coveredProviderIds={coveredProviderIds}
          isLoadingCoveredData={isLoadingCoveredProviders}
        />
      ),
    },
    {
      title: formatMessage({ id: 'form.createAutomation.highlightImportantKpis.title' }),
      description: formatMessage({ id: 'form.createAutomation.highlightImportantKpis.description' }, { providerType }),
      fieldnames: [],
      canGoNext: () => true,
      hasSkipButton: true,
      customOnClickSkip: () => {
        setValue(BulkCreateExpectationFormField.ImportantKpis, [])
        setActiveStep(current => current + 1)
      },
      footerContent: <ImportantKpisFooterContent />,
      children: (
        <HighlightImportantKpis
          fieldName={BulkCreateExpectationFormField.ImportantKpis}
          assessmentTemplateId={selectedTemplateId ?? undefined}
        />
      ),
    },
    {
      title: formatMessage({ id: 'bulkCreateExpectation.steps.deadline.title' }),
      description: formatMessage({ id: 'bulkCreateExpectation.steps.deadline.description' }, { providerType }),
      fieldnames: [BulkCreateExpectationFormField.Deadline],
      canGoNext: () => isFutureDate(getValues(BulkCreateExpectationFormField.Deadline)),
      children: <SetDeadlineStep />,
    },
    {
      title: formatMessage({ id: 'bulkCreateExpectation.steps.summary.title' }),
      description: formatMessage({ id: 'bulkCreateExpectation.steps.summary.description' }),
      fieldnames: [],
      overrideButtonTextId: 'bulkCreateExpectation.actions.send',
      isLoading: isFetchingTemplate || isSubmitting,
      canGoNext: () => !isLoadingCoveredProviders && canSubmit(getValues()),
      children: (
        <SummaryStep
          eligibleCount={
            getEligibleSelection(
              getValues(BulkCreateExpectationFormField.ProviderIds),
              initialProviders,
              getValues(BulkCreateExpectationFormField.AssessmentTemplateId),
              getValues(BulkCreateExpectationFormField.TargetAssessmentLevel),
              coveredProviderIds,
            ).length
          }
          importantKpisCount={countSelectedKpis(getValues(BulkCreateExpectationFormField.ImportantKpis))}
          assessmentTemplate={selectedTemplate}
        />
      ),
    },
  ]

  const activeQuestion = useMemo(() => QUESTIONS[activeStep - 1], [activeStep, QUESTIONS])

  useEffect(() => {
    setTotalSteps(QUESTIONS.length)
    if (QUESTIONS.length < activeStep) setActiveStep(QUESTIONS.length)
  }, [QUESTIONS.length])

  return (
    <Dialog open={open} onClose={onClose} fullScreen closeAfterTransition>
      <CreationModalContainer title={formatMessage({ id: 'bulkCreateExpectation.modalTitle' })} onClose={onClose}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Stack flexGrow={1} alignItems="center">
              {activeQuestion && (
                <CreationModalStep
                  title={activeQuestion.title}
                  description={activeQuestion.description}
                  fieldnames={activeQuestion.fieldnames}
                  canGoNext={activeQuestion.canGoNext}
                  overrideButtonTextId={activeQuestion.overrideButtonTextId}
                  isLoading={activeQuestion.isLoading}
                  hasSkipButton={activeQuestion.hasSkipButton}
                  customOnClickSkip={activeQuestion.customOnClickSkip}
                  footerContent={activeQuestion.footerContent}
                >
                  {activeQuestion.children}
                </CreationModalStep>
              )}
            </Stack>
          </form>
        </FormProvider>
      </CreationModalContainer>
    </Dialog>
  )
}

export default BulkCreateExpectationModal
